import { useState, useCallback, useEffect } from "react";
import {Grid, GridColumn,  GridSelectionChangeEvent, GridHeaderSelectionChangeEvent,
    getSelectedState, GridPageChangeEvent,  GridSortChangeEvent
    
} from "@progress/kendo-react-grid";
import { Pager, PageChangeEvent } from "@progress/kendo-react-data-tools";
import { orderBy, SortDescriptor } from "@progress/kendo-data-query";
import { getter } from "@progress/kendo-react-common";
import * as React from "react";
import { IPageState } from "./IPageState"
import { GridSelectionType } from "./SelectionType";
import { _Styles } from "../../../content/styles/Page.styles";
import { IGridChildProps } from "./IGridChildProps";
import { applyDescriptiveGridCheckboxLabels } from "../common/util";

/**
 * NextGenKendoGrid Component.
 * @function component
 */
const NextGenKendoChildGrid: React.FunctionComponent<IGridChildProps> = (props: IGridChildProps) => {
    const detailIdGetter = getter(props.parent.kendoGridProps.dataItemKey === "undefined" ? "id" : props.parent.kendoGridProps.dataItemKey);
    const [detailSelectedCheckState, setDetailSelectedCheckState] = useState<{ [id: string]: boolean | number[]; }>({});
    const detailGridInitialSort: Array<SortDescriptor> = [{ field: (props.parent.kendoGridProps.detailGrid?.initialSortField || "id"), dir: "asc" }];
    const [detailsort, setDetailSort] = useState(detailGridInitialSort);
    const initialDataStateChildGrid: IPageState = { skip: 0, take: props.parent.kendoGridProps.detailGrid?.noOfRecords };
    const [childGridPage, setChildGridPage] = useState<IPageState>(initialDataStateChildGrid);
    const [childGriddata, setChildGridData] = useState<any[]>([]);
    useEffect(() => {
        if (props.childGridData) {
            setChildGridData(props.childGridData);
        }
    }, [props.childGridData])

    useEffect(() => {
        if (props.defaultChildGridSelectionstate) {
            setDetailSelectedCheckState(props.defaultChildGridSelectionstate);
        }
    }, [props.defaultChildGridSelectionstate])
    useEffect(() => {
        // Detail grid checkboxes mount only when a master row is expanded and re-render on
        // selection/data changes without re-running the parent grid's labeling effect. Re-apply
        // descriptive accessible names here so every detail-row checkbox has a label
        // (WCAG 4.1.2 - label: Ensure every form element has a label).
        applyDescriptiveGridCheckboxLabels();
        // Kendo re-adds <colgroup role="presentation"> on every detail-grid render, but
        // role="presentation" is not an allowed ARIA role for <colgroup> (axe aria-allowed-role,
        // WCAG 1.3.1/4.1.2). The parent grid's gridAccessibilityFix() strips it but does not re-run
        // on child-only re-renders, so remove the invalid role here too. Presentation-only change.
        document.querySelectorAll('colgroup[role="presentation"]').forEach((colgroup) => {
            colgroup.removeAttribute("role");
        });
    });
    //--event for checkbox header selection in the product search child grid: when header checked all the child checkbox will be checked
    const onDetailGridHeaderSelectionChange = useCallback(
        (event: GridHeaderSelectionChangeEvent) => {
            const checkboxElement: any = event.syntheticEvent.target
            const checked = checkboxElement.checked;
            let newSelectedState: any = {} = detailSelectedCheckState;
            var data = props.parent.kendoGridProps.selectAllRows ? childGriddata : event.dataItems;
            data.forEach(item => {
                newSelectedState[detailIdGetter(item)] = checked;
            });
            setDetailSelectedCheckState(newSelectedState);
            props.parent.onDetailRowSelectionCallBack?.(newSelectedState, event.dataItems);
        }, [detailSelectedCheckState]);
    //event to trigger on change of checkbox in the grid
    const onDetailGridSelectionChange = useCallback(
        (event: GridSelectionChangeEvent) => {
            const checkboxElement: any = event.syntheticEvent.target
            const checked = checkboxElement.checked;
            let newSelectedState: any = {} = detailSelectedCheckState;
            newSelectedState[detailIdGetter(event.dataItem)] = checked;
            setDetailSelectedCheckState(newSelectedState);
            props.parent.onDetailRowSelectionCallBack?.(newSelectedState, event.dataItems);
        },
        [detailSelectedCheckState]
    );
    const onPageChangeChildGrid = (event: GridPageChangeEvent) => {
        setChildGridPage(event.page);
    };
    // Render the pager as a sibling of the Grid (not inside role="grid") to satisfy the
    // aria-required-children accessibility rule (WCAG 1.3.1). Adapt the Pager event to the
    // GridPageChangeEvent shape the handler expects.
    const onExternalChildPageChange = (event: PageChangeEvent) => {
        onPageChangeChildGrid({
            ...event,
            page: { skip: event.skip, take: event.take },
        } as unknown as GridPageChangeEvent);
    };
    const childPageSizes = props.parent.kendoGridProps.detailGrid.pageSizes;
    return (
        <React.Fragment>
            <Grid data={orderBy(childGriddata, detailsort)?.map((item: any) =>
                ({ ...item, [props.parent.kendoGridProps.detailGrid.selectedField]: detailSelectedCheckState[detailIdGetter(item)] })).slice
                (childGridPage.skip, childGridPage.take + childGridPage.skip)}
                skip={childGridPage.skip}
                take={childGridPage.take}
                selectedField={props.parent.kendoGridProps.detailGrid.selectedField}
                dataItemKey={props.parent.kendoGridProps.detailGrid.dataItemKey}
                selectable={{
                    enabled: true, drag: false, cell: false,
                    mode: props.parent.kendoGridProps.detailGrid.selectionType === GridSelectionType.Mulitple ? 'multiple' : 'single',
                }}
                resizable={props.parent.kendoGridProps.detailGrid.resizable}
                sortable={props.parent.kendoGridProps.detailGrid.sortable}
                total={childGriddata?.length}
                pageable={false}
                filterable={props.parent.kendoGridProps.detailGrid.filterable}
                onSelectionChange={onDetailGridSelectionChange}
                onHeaderSelectionChange={onDetailGridHeaderSelectionChange}
                onPageChange={onPageChangeChildGrid}
                onSortChange={(e: GridSortChangeEvent) => { setDetailSort(e.sort); }}
                >
                {props.parent.kendoGridProps.detailGrid.actionTypes?.showSelectAllCheckboxGridAction &&
                    <GridColumn field={props.parent.kendoGridProps.detailGrid.selectedField}
                        headerSelectionValue={
                            props.childGridData.findIndex((item: any) => !detailSelectedCheckState[detailIdGetter(item)]) === -1
                        }
                        filterable={false} />
                }
                {props.parent.kendoGridProps.detailGrid.columns.map((col) => {
                    if (col.isVisible)
                        return <GridColumn
                            key={col.key} field={col.apiField} title={col.columnTitle} width={col.width}
                            editable={col.isEditable} sortable={col.allowSorting} filterable={col.allowFilter}
                            editor={col.editorType} className={col.cssClass}
                        />
                })}
            </Grid>
            {props.parent.kendoGridProps.detailGrid.pageable && (
                <Pager
                    skip={childGridPage.skip}
                    take={childGridPage.take}
                    total={childGriddata?.length}
                    buttonCount={5}
                    info={true}
                    type="numeric"
                    previousNext={true}
                    pageSizes={Array.isArray(childPageSizes) ? childPageSizes : undefined}
                    onPageChange={onExternalChildPageChange}
                    className="k-grid-pager"
                />
            )}
        </React.Fragment>
    )
}
export default NextGenKendoChildGrid;
